import { createFileRoute, Outlet, redirect, useLocation, useRouter } from '@tanstack/react-router'
import { useEffect } from 'react'

import { queryClient } from '@/lib/query-client'
import { destinoDaGuarda, sessaoQuery, useSessao } from '@/lib/sessao'

import { Casca } from '@/components/gestor/Casca'

/*
 * Raiz da Área do Gestor (`/gestor/*`): a guarda e a casca.
 *
 * A guarda pergunta à API quem está na sessão antes de a tela montar e manda
 * a pessoa para onde ela pode estar (`destinoDaGuarda`): sem sessão, a
 * entrada; com a senha inicial por trocar, a troca. Por isso nenhuma tela de
 * dentro chega a pedir dado de pessoa sem sessão. A mesma regra roda de novo
 * na tela, porque a sessão pode cair com a página aberta.
 *
 * A entrada e a troca de senha ficam fora da casca (não há menu para quem
 * ainda não pode usar as abas); o resto abre dentro dela.
 */
function RaizDoGestor() {
  const { data: sessao } = useSessao()
  const caminho = useLocation({ select: (local) => local.pathname.replace(/(.)\/+$/, '$1') })
  const router = useRouter()
  // Ao sair para o observatório, a URL já é "/" enquanto esta tela ainda está
  // montada; sem o filtro, a guarda leria "/" como tela da gestão e mandaria a
  // pessoa de volta para a entrada.
  const naGestao = caminho === '/gestor' || caminho.startsWith('/gestor/')
  const destino = naGestao ? destinoDaGuarda(sessao, caminho) : null

  useEffect(() => {
    if (destino !== null) router.history.replace(destino)
  }, [destino, router])

  if (destino !== null) return null
  if (!sessao || sessao.trocarSenha) return <Outlet />

  return (
    <Casca sessao={sessao} caminho={caminho}>
      <Outlet />
    </Casca>
  )
}

function TelaNaoEncontrada() {
  return (
    <section>
      <h1 className="m-0 text-3xl font-bold text-foreground">Tela não encontrada</h1>
      <p className="mt-2 text-base text-muted-foreground">
        Esta tela não existe nesta versão da Área do Gestor.
      </p>
    </section>
  )
}

export const Route = createFileRoute('/gestor')({
  beforeLoad: async ({ location }) => {
    // Cache primeiro: depois do login ou da saída a sessão já está nele.
    const sessao = await queryClient.ensureQueryData(sessaoQuery)
    const destino = destinoDaGuarda(sessao, location.pathname)
    // O roteador lança o objeto de redirecionamento, que não é um `Error`.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    if (destino !== null) throw redirect({ href: destino, replace: true })
  },
  component: RaizDoGestor,
  notFoundComponent: TelaNaoEncontrada,
})
